import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/api';
import { otpSessionIsExpired } from '../../lib/otpSession';
import {
  isPasswordRecoveryMode,
  setPasswordRecoveryMode,
  useAuth,
} from '../../contexts/AuthContext';
import Spinner from '../../components/ui/Spinner';

/** Snapshot of the hash at module load, before detectSessionInUrl may strip it. */
const MODULE_HASH = typeof window !== 'undefined' ? window.location.hash : '';

const SIGN_IN_WAIT_MS = 15000;
const PROFILE_WAIT_MS = 15000;

function parseHashTokens(hash) {
  const params = new URLSearchParams((hash || '').replace(/^#/, ''));
  return {
    access_token: params.get('access_token'),
    refresh_token: params.get('refresh_token'),
  };
}

function readHandoffTokens() {
  const fromWindow = parseHashTokens(typeof window !== 'undefined' ? window.location.hash : '');
  if (fromWindow.access_token && fromWindow.refresh_token) return fromWindow;
  return parseHashTokens(MODULE_HASH);
}

function stripAuthFragment() {
  if (typeof window === 'undefined') return;
  window.history.replaceState(null, '', window.location.pathname);
}

/** A CRM login requires a real users row, not merely a Supabase session. */
function isResolvedCrmProfile(profile) {
  return Boolean(
    profile &&
      profile.id &&
      profile.agency_id &&
      profile.role &&
      profile.is_active === true,
  );
}

const signingInSpinner = (
  <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 text-gray-600 gap-3">
    <Spinner size="lg" className="text-primary h-8 w-8" />
    <p className="text-sm">Signing you in...</p>
  </div>
);

export default function SsoHandoff() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [awaitingDashboard, setAwaitingDashboard] = useState(false);
  const finishedRef = useRef(false);
  const logoutRef = useRef(logout);
  const refreshUserRef = useRef(refreshUser);
  const navigateRef = useRef(navigate);
  logoutRef.current = logout;
  refreshUserRef.current = refreshUser;
  navigateRef.current = navigate;

  useEffect(() => {
    if (!supabase) {
      navigateRef.current('/login', { replace: true });
      return undefined;
    }

    const tokens = readHandoffTokens();
    if (!tokens.access_token || !tokens.refresh_token) {
      navigateRef.current('/login', { replace: true });
      return undefined;
    }

    let cancelled = false;
    let eventWaitTimeout;
    let eventSubscription;

    const fail = async (to) => {
      if (cancelled || finishedRef.current) return;
      finishedRef.current = true;
      setPasswordRecoveryMode(false);
      try {
        await logoutRef.current();
      } catch {
        // Session may already be gone.
      }
      if (!cancelled) navigateRef.current(to, { replace: true });
    };

    const waitForAuthEvent = () =>
      new Promise((resolve) => {
        let settled = false;
        const finish = (event) => {
          if (settled) return;
          settled = true;
          if (eventWaitTimeout) window.clearTimeout(eventWaitTimeout);
          if (eventSubscription) eventSubscription.unsubscribe();
          resolve(event);
        };
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((event) => {
          if (event !== 'SIGNED_IN' && event !== 'PASSWORD_RECOVERY') return;
          finish(event);
        });
        eventSubscription = subscription;
        eventWaitTimeout = window.setTimeout(() => finish(null), SIGN_IN_WAIT_MS);
      });

    const run = async () => {
      const eventPromise = waitForAuthEvent();

      try {
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
        });
        stripAuthFragment();

        if (cancelled || finishedRef.current) return;

        if (sessionError || !data?.session) {
          await fail('/login?expired=1');
          return;
        }

        const authEvent = await eventPromise;
        if (cancelled || finishedRef.current) return;

        // Recovery sessions are not CRM logins — same discipline as commit 6297cf7.
        if (authEvent === 'PASSWORD_RECOVERY' || isPasswordRecoveryMode()) {
          await fail('/login?expired=1');
          return;
        }

        if (authEvent !== 'SIGNED_IN') {
          await fail('/login?expired=1');
          return;
        }

        // Same 24h OTP cap as AuthContext — a code-based session handed off here
        // must not reach /dashboard if otp_login_at is already stale.
        let authUser = data.session.user;
        try {
          const { data: remote } = await supabase.auth.getUser();
          if (remote?.user) authUser = remote.user;
        } catch {
          // Fall back to the session user from setSession.
        }
        if (otpSessionIsExpired(authUser)) {
          await fail('/login?error=otp_expired');
          return;
        }

        const profile = await refreshUserRef.current();
        if (cancelled || finishedRef.current) return;

        if (!isResolvedCrmProfile(profile)) {
          await fail('/login?error=profile');
          return;
        }

        setAwaitingDashboard(true);
      } catch {
        stripAuthFragment();
        await fail('/login?expired=1');
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (eventWaitTimeout) window.clearTimeout(eventWaitTimeout);
      if (eventSubscription) eventSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!awaitingDashboard) return undefined;
    if (isResolvedCrmProfile(user)) {
      finishedRef.current = true;
      navigate('/dashboard', { replace: true });
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      setPasswordRecoveryMode(false);
      void logoutRef
        .current()
        .catch(() => {})
        .finally(() => navigateRef.current('/login?error=profile', { replace: true }));
    }, PROFILE_WAIT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [awaitingDashboard, user, navigate]);

  return signingInSpinner;
}
